import classNames from 'classnames';
import type {
  ComponentProps,
  HTMLAttributes,
  MouseEvent,
  MouseEventHandler,
} from 'react';
import React, { forwardRef, useEffect, useId, useRef, useState } from 'react';
import type { DistributedOmit, SetOptional } from 'type-fest';
import addTestId from '../utils/addTestId';
import Link from '../utils/router/Link';
import trackMouseLeaveIntent from '../utils/trackMouseLeaveIntent';
import useHover from '../utils/useHover';
import type { CaretDirection } from './Caret';
import Caret from './Caret';
import * as styles from './Menu.module.scss';
import SVGIcon from './SVGIcon';
import Slot from './Slot';
import Tether from './Tether';
import type { TetherRef } from './Tether';
import Tooltip from './Tooltip';
import PopOverContainer from './deprecated/PopOverContainer';
import CheckRadioControl from './formInternals/CheckRadioControl';
import ToggleControl from './formInternals/ToggleControl';

export type MenuItemElement = HTMLDivElement & HTMLAnchorElement;

interface MenuItemCaretProps {
  direction: CaretDirection;
}

const MenuItemCaret = ({ direction }: MenuItemCaretProps) => (
  <div aria-hidden="true" role="img" className={styles.caret}>
    <Caret direction={direction} size="small" />
  </div>
);

type BaseMenuItemProps = {
  children: React.ReactNode;
  danger?: boolean;
  disabled?: boolean;
  icon?: React.ReactNode;
  right?: React.ReactNode;
  tooltip?: string;
  tooltipPlacement?: React.ComponentProps<typeof Tooltip>['placement'];
  wrap?: boolean;
  /**
   * If true, clicking this menu item will not close the menu.
   * Useful for items with interactive elements (e.g., checkboxes, submenus)
   *
   * @TODO: this should be considered as a workaround and seems only needed in OverflowMenu.
   * A better approach is to handle the logic in OverflowMenu by checking what is being clicked.
   */
  preventCloseOnClick?: boolean;
} & (
  | {
      to: React.ComponentProps<typeof Link>['to'];
      state?: React.ComponentProps<typeof Link>['state'];
      newTab?: React.ComponentProps<typeof Link>['newTab'];
    }
  | { to?: never; state?: never; newTab?: never }
) &
  Pick<
    HTMLAttributes<HTMLElement>,
    | 'aria-expanded'
    | 'aria-checked'
    | 'aria-haspopup'
    | 'aria-selected'
    | 'className'
    | 'id'
    | 'role'
    | 'onClick'
    | 'onMouseEnter'
    | 'onMouseLeave'
  >;

const BaseMenuItem = forwardRef<MenuItemElement, BaseMenuItemProps>(
  (
    {
      children,
      danger = false,
      disabled = false,
      icon,
      right,
      tooltip,
      tooltipPlacement = 'left',
      wrap = false,
      to,
      state,
      newTab,
      preventCloseOnClick,
      onClick,
      ...props
    },
    ref,
  ) => {
    const commonProps = {
      ...props,
      onClick: preventCloseOnClick
        ? (e) => {
            e.stopPropagation();
            onClick?.(e);
          }
        : onClick,
      ref,
      'aria-disabled': disabled ?? undefined,
      children: (
        <div className={styles.content}>
          {!!icon && (
            <Slot aria-hidden="true" role="img">
              {typeof icon === 'string' ? (
                <SVGIcon className={styles.icon}>{icon}</SVGIcon>
              ) : (
                <div className={styles.icon}>{icon}</div>
              )}
            </Slot>
          )}
          <div
            className={classNames(styles.main, {
              [styles.wrap]: wrap,
            })}
          >
            {children}
          </div>
          {!!right && <div className={styles.right}>{right}</div>}
        </div>
      ),
      className: classNames(styles.item, props.className, {
        [styles.danger]: danger,
      }),
      tabIndex: -1,
    } as const satisfies Partial<
      ComponentProps<typeof Link> & HTMLAttributes<HTMLDivElement>
    >;

    const content = to ? (
      // eslint-disable-next-line jsx-a11y/anchor-has-content -- we're using children
      <Link {...commonProps} to={to} state={state} newTab={newTab} />
    ) : (
      <div {...commonProps} />
    );
    return tooltip ? (
      <Tooltip placement={tooltipPlacement} content={tooltip} display="block">
        {content}
      </Tooltip>
    ) : (
      content
    );
  },
);

type MenuItemRadioOrCheckboxProps = BaseMenuItemProps & {
  checked?: boolean;
  testId?: string;
  onClick?: MouseEventHandler;
  role: 'menuitemradio' | 'menuitemcheckbox';
  /**
   * The variant of the menu item.
   * - `checkmark`: A checkmark icon
   * - `checkbox`: A checkbox control. Clicking the menu item will not close the menu.
   * - `toggle`: A toggle control. Clicking the menu item will not close the menu.
   * - `highlight`: icon and text are highlighted.
   */
  variant: 'checkmark' | 'checkbox' | 'toggle' | 'highlight';
};

const MenuItemRadioOrCheckbox = React.forwardRef<
  MenuItemElement,
  MenuItemRadioOrCheckboxProps
>(
  (
    { id, checked, role, testId, onClick, variant, children, right, ...props },
    ref,
  ) => {
    let content = children;
    if (variant === 'checkbox') {
      content = (
        <div aria-hidden="true" className={styles.checkboxContentContainer}>
          <CheckRadioControl
            type="checkbox"
            checked={checked}
            readOnly
            {...props}
          />
          {children}
        </div>
      );
    } else if (variant === 'toggle') {
      content = <div className={styles.toggleContentContainer}>{children}</div>;
    } else if (variant === 'highlight') {
      content = (
        <div className={styles.highlightContentContainer}>{children}</div>
      );
    }

    let finalRight = right;
    if (variant === 'checkmark') {
      finalRight = <div aria-hidden="true" className={styles.checkmark} />;
    } else if (variant === 'toggle') {
      finalRight = (
        <div aria-hidden="true" className={styles.toggleControlContainer}>
          <ToggleControl
            type="checkbox"
            checked={checked}
            readOnly
            config={{ knobSize: 16, trackWidth: 24 }}
          />
        </div>
      );
    }

    return (
      <BaseMenuItem
        ref={ref}
        id={id}
        role={role}
        aria-checked={checked}
        right={finalRight}
        preventCloseOnClick={variant === 'checkbox' || variant === 'toggle'}
        onClick={onClick}
        {...props}
        {...addTestId(testId)}
      >
        {content}
      </BaseMenuItem>
    );
  },
);

type MenuItemRadioProps = SetOptional<
  DistributedOmit<MenuItemRadioOrCheckboxProps, 'role'>,
  'variant'
>;

/**
 * A radio menu item that can be selected or unselected.
 * Unlike MenuItemCheckbox which allows multiple selections, only one radio item can be selected within a group.
 * Note: This component only provides the radio button appearance and ARIA role - you'll need to implement the exclusive selection logic yourself.
 */
export const MenuItemRadio = React.forwardRef<
  MenuItemElement,
  MenuItemRadioProps
>(({ variant, ...props }, ref) => (
  <MenuItemRadioOrCheckbox
    ref={ref}
    role="menuitemradio"
    variant={variant ?? 'checkmark'}
    {...props}
  />
));

type MenuItemCheckboxProps = SetOptional<
  DistributedOmit<MenuItemRadioOrCheckboxProps, 'role'>,
  'variant'
>;

/**
 * A checkbox menu item that can be checked or unchecked.
 * Similar to `MenuItemRadio`, but this semantically allows multiple items to be checked.
 */
export const MenuItemCheckbox = React.forwardRef<
  MenuItemElement,
  MenuItemCheckboxProps
>(({ variant, ...props }, ref) => (
  <MenuItemRadioOrCheckbox
    ref={ref}
    role="menuitemcheckbox"
    variant={variant ?? 'checkbox'}
    {...props}
  />
));

type MenuItemProps = DistributedOmit<BaseMenuItemProps, 'right'> & {
  testId?: string;
  onClick?: MouseEventHandler;
} & (
    | { submenuItems?: React.ReactNode; right?: never }
    | { submenuItems?: never; right?: BaseMenuItemProps['right'] }
  );

/**
 * A normal menu item that can have a submenu.
 *
 * @example
 * <Menu>
 *   <MenuItem onClick={() => {}}>Action</MenuItem>
 *   <MenuItem to="/path">Navigation</MenuItem>
 *   <MenuItem submenuItems={<>
 *     <MenuItem>Submenu item 1</MenuItem>
 *     <MenuItem>Submenu item 2</MenuItem>
 *   </>}>
 *     Submenu
 *   </MenuItem>
 * </Menu>
 */
export const MenuItem = React.forwardRef<MenuItemElement, MenuItemProps>(
  ({ submenuItems, testId, onClick, ...props }: MenuItemProps, ref) => {
    const [isSubmenuExpanded, setIsSubmenuExpanded] = useState(false);

    const propsForBase = {
      ...addTestId(testId),
      role: 'menuitem',
      onClick,
      ...props,
    } satisfies Partial<BaseMenuItemProps>;

    if (!submenuItems) {
      return <BaseMenuItem ref={ref} {...propsForBase} />;
    }

    const propsForMenuItemWithSubmenu = {
      ...propsForBase,
      submenuExpanded: isSubmenuExpanded,
      onSubmenuExpandedChange: setIsSubmenuExpanded,
      'aria-expanded': isSubmenuExpanded,
      'aria-haspopup': 'menu',
    } satisfies Partial<MenuItemWithSubmenuProps>;

    return (
      <MenuItemWithSubmenu
        ref={ref}
        {...propsForMenuItemWithSubmenu}
        submenuItems={submenuItems}
      />
    );
  },
);

type MenuItemWithSubmenuProps = BaseMenuItemProps & {
  submenuItems: React.ReactNode;
  submenuExpanded: boolean;
  onSubmenuExpandedChange: (isExpanded: boolean) => void;
};

const MenuItemWithSubmenu = React.forwardRef<
  MenuItemElement,
  MenuItemWithSubmenuProps
>(
  (
    {
      submenuItems,
      submenuExpanded,
      onSubmenuExpandedChange,
      className,
      ...props
    },
    ref,
  ) => {
    const submenuId = useId();

    // When a submenu is open, disable other menu items to prevent them from being
    // highlighted as the mouse moves across them while navigating to the submenu.
    const [shouldDisableOtherMenuItems, setShouldDisableOtherMenuItems] =
      useState(false);

    const cleanUpMouseLeaveTrackingRef = useRef(() => {});
    useEffect(() => {
      return () => {
        cleanUpMouseLeaveTrackingRef.current();
      };
    }, []);

    const {
      onMouseEnter: originalOnMouseEnter,
      onMouseLeave: originalOnMouseLeave,
      setHover,
    } = useHover({
      hoverDelay: 'fast',
      hoverGroupId: 'menu',
      onHoverChange(hoveringId) {
        onSubmenuExpandedChange(hoveringId === submenuId);
      },
    });

    const tetherRef = useRef<TetherRef>(null);

    const onMouseEnter = (event: MouseEvent) => {
      cleanUpMouseLeaveTrackingRef.current();
      originalOnMouseEnter(submenuId, event);
    };

    const onMouseLeave = (event: MouseEvent) => {
      cleanUpMouseLeaveTrackingRef.current();

      const handleIntentionalMouseLeave = () => {
        originalOnMouseLeave(submenuId);
        setShouldDisableOtherMenuItems(false);
      };

      if (tetherRef.current?.positionedElement) {
        cleanUpMouseLeaveTrackingRef.current = trackMouseLeaveIntent({
          initialMousePosition: { x: event.clientX, y: event.clientY },
          targetRect:
            tetherRef.current.positionedElement.getBoundingClientRect(),
          onLeave: handleIntentionalMouseLeave,
        });
      } else {
        handleIntentionalMouseLeave();
      }
    };

    const [anchorElement, setAnchorElement] = useState<HTMLElement | null>(
      null,
    );

    return (
      <>
        <Slot ref={setAnchorElement}>
          <BaseMenuItem
            ref={ref}
            {...props}
            className={classNames(className, {
              [styles.disableOtherMenuItems]: shouldDisableOtherMenuItems,
            })}
            right={<MenuItemCaret direction="right" />}
            onMouseEnter={(event) => {
              setShouldDisableOtherMenuItems(true);
              onMouseEnter(event);
            }}
            onMouseLeave={onMouseLeave}
            preventCloseOnClick
          />
        </Slot>
        <Tether
          ref={tetherRef}
          anchorElement={anchorElement}
          placement="left-start"
          offset={5}
          positionedView={
            submenuExpanded && (
              <div
                className={styles.submenuContainer}
                onMouseEnter={(event) => {
                  setShouldDisableOtherMenuItems(false);
                  onMouseEnter(event);
                }}
                onMouseLeave={onMouseLeave}
                onClick={() => setHover(null)}
              >
                <Menu>{submenuItems}</Menu>
              </div>
            )
          }
        />
      </>
    );
  },
);

interface MenuSimpleItemProps {
  children: React.ReactNode;
}

export const MenuSimpleItem = ({ children }: MenuSimpleItemProps) => (
  <div className={styles.simpleItem}>{children}</div>
);

interface MenuSeparatorProps {
  fullWidth?: boolean;
}

export const MenuSeparator = ({ fullWidth = false }: MenuSeparatorProps) => (
  <div
    role="separator"
    className={classNames(styles.separator, {
      [styles.fullWidth]: fullWidth,
    })}
  />
);

interface MenuSectionProps {
  children: React.ReactNode;
  header: string;
}

/**
 * Group a list of menu items together with a header.
 *
 * @example
 * <Menu>
 *   <MenuSection header="Group 1">
 *     <MenuItem>Item 1</MenuItem>
 *     <MenuItem>Item 2</MenuItem>
 *   </MenuSection>
 * </Menu>
 */
export const MenuSection = ({ children, header }: MenuSectionProps) => (
  <section role="group" className={styles.section}>
    <header role="presentation" className={styles.sectionHeader}>
      {header}
    </header>
    {children}
  </section>
);
interface MenuProps {
  children: React.ReactNode;
  ariaLabel?: string;
  style?: React.CSSProperties;
}

const Menu = forwardRef<HTMLDivElement, MenuProps>(
  ({ children, ariaLabel, style }, ref) =>
    children ? (
      <PopOverContainer noPadding width="auto" fullHeight>
        <div
          ref={ref}
          role="menu"
          data-no-print
          className={styles.menu}
          style={style}
          aria-label={ariaLabel}
        >
          {children}
        </div>
      </PopOverContainer>
    ) : null,
);

export default Menu;
